import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { colors } from "../../../theme/palette";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { UserInfo } from "../../../types";
import { getEventJoiningPlayers } from "../../../api/events";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { DeletePlayerIcon } from "../../../assets/icons/MUI/EventsIcons";
import { Dispatch, SetStateAction } from "react";

const JoiningPlayersList = ({
  eventId,
  setPlayerId,
  setPlayerName,
  setOpenDialog,
  isOwnEvent,
}: {
  eventId?: string;
  setPlayerId: Dispatch<SetStateAction<null | number>>;
  setPlayerName: Dispatch<SetStateAction<string>>;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  isOwnEvent: boolean;
}) => {
  const {
    data: playersListingData,
    isLoading: isPlayersListingDataLoading,
    isError: isPlayersListingDataHasError,
  } = useQuery<any>({
    queryKey: ["joiningPlayersList", eventId],
    queryFn: () => getEventJoiningPlayers(`${eventId}`),
    enabled: !!eventId,
  });

  if (isPlayersListingDataLoading) return <span>Loading...</span>;

  const playersList: UserInfo[] = playersListingData?.items;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {playersList.length > 0 &&
        playersList.map((player: UserInfo, index: number) => {
          const isLast = playersList.length === index + 1;
          return (
            <PlayerCard
              key={player.id}
              player={player}
              isLast={isLast}
              setPlayerId={setPlayerId}
              setPlayerName={setPlayerName}
              setOpenDialog={setOpenDialog}
              isOwnEvent={isOwnEvent}
            />
          );
        })}
    </Box>
  );
};

export default JoiningPlayersList;

const PlayerCard = ({
  player,
  isLast,
  setPlayerId,
  setPlayerName,
  setOpenDialog,
  isOwnEvent,
}: {
  player: UserInfo;
  isLast: boolean;
  setPlayerId: Dispatch<SetStateAction<null | number>>;
  setPlayerName: Dispatch<SetStateAction<string>>;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  isOwnEvent: boolean;
}) => {
  const { t } = useTranslation();

  const { id, fullName, firstTwoChr, media, role, club, mutualFollowersCount } = useDataFormatter(player);

  return (
    <Box
      sx={{
        display: "flex",
        maxHeight: "100%",
        overflow: "auto",
        columnGap: "20px",
        paddingBottom: "16px",
        marginBottom: isLast ? "" : "16px",
        borderBottom: isLast ? "" : "1px solid #E7E7E7",
      }}
    >
      <Avatar
        src={media}
        sx={{
          width: "47px",
          height: "47px",
        }}
      >
        {firstTwoChr}
      </Avatar>
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "80%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "80%" }}>
          <Typography
            variant="body16SemiBold"
            sx={{
              color: "#171A1C",
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fullName}
          </Typography>
          <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
            {role} . {club}
          </Typography>
          <Typography variant="body14Regular" sx={{ color: colors.Dark600 }}>
            {mutualFollowersCount} {t("pages.events.eventDetails.mutualFollowers")}
          </Typography>
        </Box>
        {isOwnEvent && (
          <IconButton
            sx={{ width: "fit-content", height: "fit-content" }}
            onClick={() => {
              if (!id || !fullName) return;
              setPlayerId(id);
              setPlayerName(fullName);
              setOpenDialog(true);
            }}
          >
            <DeletePlayerIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
